import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Check } from "lucide-react";
import { money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export const MealDealPicker = ({ open, onOpenChange, onAdd, categories: catsProp, headerNote, previewMode }) => {
  const { settings } = useSettings();
  const cfg = settings?.meal_deal || {};
  const source = catsProp || cfg.customizations || [];
  const categories = source
    .map((c) => ({ ...c, items: (c.items || []).filter((it) => it.visible !== false && (it.name || "").trim()) }))
    .filter((c) => c.items.length > 0);
  const [selected, setSelected] = useState({});

  useEffect(() => { if (open) setSelected({}); }, [open]);

  const toggle = (cat, item) => {
    setSelected((prev) => {
      const cur = prev[cat.id] || [];
      if (cur.find((x) => x.id === item.id)) return { ...prev, [cat.id]: cur.filter((x) => x.id !== item.id) };
      const max = cat.max_choices || 1;
      if (cur.length >= max) {
        if (max === 1) return { ...prev, [cat.id]: [item] };
        return prev;
      }
      return { ...prev, [cat.id]: [...cur, item] };
    });
  };

  const allSelected = Object.values(selected).flat();
  const missingRequired = categories.filter((c) => c.required && !((selected[c.id] || []).length));
  const blocked = allSelected.length === 0 || missingRequired.length > 0;
  const confirm = () => { onAdd(allSelected); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[130] max-w-md w-[95vw] bg-white rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="meal-picker">
        <VisuallyHidden><DialogTitle>Make it a meal</DialogTitle></VisuallyHidden>
        <h2 className="font-heading font-extrabold text-lg leading-tight">Make it a meal</h2>
        {headerNote && <p className="text-xs text-muted-foreground mt-1" data-testid="meal-preview-note">{headerNote}</p>}
        <div className="mt-3 space-y-4">
          {categories.map((cat) => (
            <div key={cat.id} data-testid={`meal-cat-${cat.id}`}>
              <div className="flex items-center justify-between mb-2 bg-gray-50 border-l-2 border-metros-red rounded-md px-2 py-1">
                <h4 className="font-heading font-extrabold text-sm uppercase tracking-wide text-metros-black">{cat.name}{cat.required && <span className="text-metros-red"> *</span>}</h4>
                <span className="text-xs text-muted-foreground">{cat.required ? "Required · " : ""}Choose up to {cat.max_choices || 1}</span>
              </div>
              <div className="space-y-2">
                {cat.items.map((item) => {
                  const active = !!(selected[cat.id] || []).find((x) => x.id === item.id);
                  return (
                    <button key={item.id} data-testid={`meal-opt-${item.id}`} onClick={() => toggle(cat, item)}
                      className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl border text-left transition-colors ${active ? "border-metros-red bg-red-50" : "border-gray-200 hover:border-gray-300"}`}>
                      <span className="flex-1 text-sm font-semibold text-metros-black truncate">{item.name}</span>
                      <span className="text-sm font-semibold text-metros-red">{money(item.price || 0)}</span>
                      <span className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${active ? "border-metros-red bg-metros-red text-white" : "border-gray-300"}`}>{active && <Check size={13} />}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {categories.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No meal options available.</p>}
        </div>
        {!previewMode && missingRequired.length > 0 && <p className="text-xs text-metros-red mt-3 text-center" data-testid="meal-required-note">Please choose from: {missingRequired.map((c) => c.name).join(", ")}</p>}
        <button data-testid="meal-picker-confirm" onClick={previewMode ? () => onOpenChange(false) : confirm} disabled={!previewMode && blocked}
          className="mt-4 w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3 font-bold text-sm active:scale-95 transition-all disabled:opacity-50">
          {previewMode ? "Close preview" : "Add to basket"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
